/**
 * Client evaluation of the coordinator after project completion.
 *
 * Accessible at /projects/:id/evaluate
 *
 */

import { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  useProjectView,
  useProjectParticipants,
} from '@/hooks/useProjectViews';
import { useAuthStore } from '@/stores/authStore';
import { profileImageUrl } from '@/hooks/useProfile';
import { useSubmitVotes } from '@hooks/useVotes';
import { useDaoConfig } from '@/hooks/useDaoConfig';
import { Button, Spinner, ConfirmDialog } from '@components/ui';

export default function TeamEvaluationPage(): JSX.Element {
  const { coordinatorRole } = useDaoConfig();
  const { id: projectId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: projectData, isLoading: loadingProject } =
    useProjectView(projectId);
  const submitVotes = useSubmitVotes();

  const [coordinatorRating, setCoordinatorRating] = useState(0);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const project = projectData;
  const session = useAuthStore((s) => s.session);
  const participants = useProjectParticipants(projectId ?? '');
  const coordinator = participants.data?.coordinator;

  const allRated = coordinatorRating > 0;

  // Opens the confirmation dialog — actual mutation fires only after the user confirms.
  const handleRequestSubmit = useCallback(() => {
    if (!projectId || !allRated) return;
    setIsConfirmOpen(true);
  }, [projectId, allRated]);

  // Evaluations do not release funds; milestone acceptance already settled them.
  const handleConfirmedSubmit = useCallback(() => {
    if (!projectId) return;

    submitVotes.mutate(
      { projectId, coordinatorRating },
      {
        onSuccess: () => {
          setIsConfirmOpen(false);
          navigate(`/projects/${projectId}`);
        },
        onError: () => {
          // Keep dialog open on error so the user can retry or cancel.
          setIsConfirmOpen(false);
        },
      }
    );
  }, [submitVotes, projectId, coordinatorRating, navigate]);

  const isLoading = loadingProject;

  // ---- Loading ----
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[var(--base-surface-1,#141414)]">
        <Spinner size="lg" />
      </div>
    );
  }

  // ---- Error ----
  if (!project) {
    return (
      <div className="min-h-screen bg-[var(--base-surface-1,#141414)] px-8 py-10 lg:px-[112px]">
        <button
          onClick={() => navigate(-1)}
          className="mb-6 flex items-center gap-1 text-sm text-[var(--text-dark-secondary,rgba(255,255,255,0.7))] hover:text-[var(--text-dark-primary,#f5f5f5)]"
        >
          <i className="ri-arrow-left-s-line text-lg" />
          Back
        </button>
        <div className="py-16 text-center">
          <i className="ri-error-warning-line mb-4 text-4xl text-red-400" />
          <h2 className="text-xl font-semibold text-[var(--text-dark-primary,#f5f5f5)]">
            Project not found
          </h2>
        </div>
      </div>
    );
  }

  if (participants.isPending)
    return (
      <div role="status" className="p-10">
        <Spinner /> Loading participants…
      </div>
    );
  if (participants.isError)
    return (
      <div className="p-10">
        <p role="alert">{participants.error.message}</p>
        <Button
          onClick={() => {
            void participants.refetch();
          }}
        >
          Retry
        </Button>
      </div>
    );
  if (!project.completed || session?.accountId !== project.client)
    return (
      <p role="alert" className="p-10">
        This evaluation is available to its project participant after all
        milestones complete.
      </p>
    );
  if (project.evaluations.some((e) => e.author === session?.accountId))
    return (
      <div className="p-10">
        <p role="status">Your evaluation has been saved.</p>
        <Button onClick={() => navigate('/projects/' + project.projectId)}>
          Back to project
        </Button>
      </div>
    );

  return (
    <div className="min-h-screen bg-[var(--base-surface-1,#141414)]">
      <div className="mx-auto max-w-2xl px-8 py-10 lg:px-[112px]">
        {/* Back navigation */}
        <button
          onClick={() => navigate(`/projects/${projectId}`)}
          className="mb-6 flex items-center gap-1 text-sm text-[var(--text-dark-secondary,rgba(255,255,255,0.7))] hover:text-[var(--text-dark-primary,#f5f5f5)]"
        >
          <i className="ri-arrow-left-s-line text-lg" />
          Back to Project
        </button>

        {/* Header */}
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[rgba(54,211,153,0.15)]">
            <i className="ri-star-line text-3xl text-[var(--state-brand-active,#36d399)]" />
          </div>
          <h1 className="mb-2 text-[30px] font-bold leading-[42px] text-[var(--text-dark-primary,#f5f5f5)]">
            Evaluate Your {coordinatorRole}
          </h1>
          <p className="text-sm text-[var(--text-dark-secondary,rgba(255,255,255,0.7))]">
            All milestones for{' '}
            <span className="font-medium text-[var(--state-brand-active,#36d399)]">
              {project.title}
            </span>{' '}
            have been completed. Rate your project {coordinatorRole.toLowerCase()}.
          </p>
        </div>

        {/* Coordinator rating card */}
        {coordinator && (
          <EvaluationCard
            name={
              coordinator?.profiles?.worker?.name ??
              coordinator?.displayName ??
              coordinatorRole
            }
            role={coordinatorRole}
            avatarUrl={
              coordinator?.workerImage && coordinator.profiles
                ? profileImageUrl(coordinator.profiles.principalId, 'worker')
                : undefined
            }
            rating={coordinatorRating}
            onRatingChange={setCoordinatorRating}
          />
        )}

        {/* Submit — opens confirmation dialog before firing the mutation */}
        <Button
          variant="primary"
          onClick={handleRequestSubmit}
          disabled={submitVotes.isPending || !allRated}
          className="mt-6 w-full"
        >
          Submit Review
        </Button>

        {!allRated && (
          <p className="mt-3 text-center text-xs text-[var(--text-dark-secondary,rgba(255,255,255,0.7))]">
            Please rate your {coordinatorRole.toLowerCase()} before submitting the review.
          </p>
        )}

        {submitVotes.error && (
          <p className="mt-3 text-center text-sm text-red-400">
            {submitVotes.error.message}
          </p>
        )}
      </div>

      {/* Payment release confirmation dialog */}
      <ConfirmDialog
        isOpen={isConfirmOpen}
        title="Submit evaluation"
        description="Submit these final ratings? Milestone payments have already been settled."
        confirmLabel="Submit ratings"
        confirmVariant="primary"
        isLoading={submitVotes.isPending}
        onConfirm={handleConfirmedSubmit}
        onCancel={() => setIsConfirmOpen(false)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Evaluation Card
// ---------------------------------------------------------------------------

interface EvaluationCardProps {
  name: string;
  role: string;
  avatarUrl?: string;
  rating: number;
  onRatingChange: (value: number) => void;
}

function EvaluationCard({
  name,
  role,
  avatarUrl,
  rating,
  onRatingChange,
}: EvaluationCardProps): JSX.Element {
  const [hovered, setHovered] = useState(0);

  return (
    <div className="mb-4 rounded-[var(--radi-6,12px)] border border-[var(--base-border,#3d3d3d)] bg-[var(--base-surface-2,#231f1f)] p-6">
      <div className="mb-4 flex items-center gap-4">
        {/* 56px avatar */}
        <div className="h-14 w-14 flex-shrink-0 overflow-hidden rounded-full border-2 border-[var(--base-border,#3d3d3d)] bg-[var(--base-fill-2,#3d3d3d)]">
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={name}
              className="h-full w-full object-cover"
              onError={(e) => {
                (e.target as HTMLImageElement).src = '/images/none.png';
              }}
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <i className="ri-user-line text-2xl text-[var(--text-dark-secondary,rgba(255,255,255,0.7))]" />
            </div>
          )}
        </div>
        <div>
          <p className="text-base font-semibold text-[var(--text-dark-primary,#f5f5f5)]">
            {name}
          </p>
          <p className="text-sm text-[var(--text-dark-secondary,rgba(255,255,255,0.7))]">
            {role}
          </p>
        </div>
      </div>

      {/* Star rating */}
      <div className="flex items-center gap-1">
        {Array.from({ length: 5 }, (_, i) => {
          const starValue = i + 1;
          const filled = starValue <= (hovered || rating);
          return (
            <button
              key={i}
              type="button"
              aria-label={`${starValue} stars for ${name}`}
              aria-pressed={rating === starValue}
              onClick={() => onRatingChange(starValue)}
              onMouseEnter={() => setHovered(starValue)}
              onMouseLeave={() => setHovered(0)}
              className="p-1 transition-transform hover:scale-110 focus:outline-none"
            >
              <svg
                className={`h-8 w-8 transition-colors ${
                  filled
                    ? 'text-[var(--state-brand-active,#36d399)]'
                    : 'text-[rgba(255,255,255,0.15)]'
                }`}
                viewBox="0 0 16 16"
                fill="currentColor"
              >
                <path d="M8 1.333l1.885 4.347 4.782.427-3.614 3.08 1.117 4.646L8 11.347l-4.17 2.486 1.117-4.646-3.614-3.08 4.782-.427L8 1.333z" />
              </svg>
            </button>
          );
        })}
        <span className="ml-3 text-sm font-medium text-[var(--text-dark-secondary,rgba(255,255,255,0.7))]">
          {rating}/5
        </span>
      </div>
    </div>
  );
}
